// AIChatResponse.js
// COMPONENTE: Renderizza un messaggio chat (utente o AI) con markdown, code highlight,
// gestione del pensiero nascosto (<think>) e piccoli badge di stato.
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { Box, Typography, Avatar, Chip, Button, Collapse } from "@material-ui/core";

import Lottie from "react-lottie";
import robotThinking from "./Robot-Bot 3D.json";

const AIChatResponse = ({ text, isUser, context = {}, isLoading }) => {
  const [showThinking, setShowThinking] = useState(false);

  const isValid = (value) => {
    // Valida che il valore sia informativo e non un placeholder
    const invalidValues = [
      "nessuno",
      "non specificata",
      "da specificare",
      "tutti",
      "",
      " ",
      "tipo di medico",
      "azienda",
    ];
    return value && typeof value === "string" && !invalidValues.includes(value.toLowerCase().trim());
  };

  const cleanValue = (value) => {
    // Pulisce un valore di contesto per visualizzazione umana
    if (!value || typeof value !== "string") return "";
    return value
      .replace(/\\n/g, "")
      .replace(/\s+/g, " ")
      .replace(/\*\*/g, "")
      .replace(/_/g, "")
      .trim();
  };

  const cleanTextForDisplay = (text) => {
    // Funzione critica per la pulizia del testo grezzo ricevuto dall'LLM.
    // L'LLM può generare token speciali o blocchi XML che non devono essere visti dall'utente.
    if (!text || typeof text !== "string") return "";
    return text
      // Rimuove il blocco <context>...</context> che contiene metadati per il sistema (es. prodotto rilevato)
      .replace(/<context>[\s\S]*?<\/context>/g, "")
      // Rimuove il blocco <think>...</think> che contiene il ragionamento interno (Chain of Thought)
      .replace(/<think>[\s\S]*?<\/think>/gi, "") 
      // Normalizza i ritorni a capo eccessivi (più di 2 diventano 2) per una formattazione pulita
      .replace(/\n\n+/g, "\n\n")
      .trim();
  };

  // Logica per estrarre il "Pensiero" (Chain of Thought)
  // Alcuni modelli (es. DeepSeek-R1) generano un blocco <think> con il loro ragionamento.
  // Lo estraiamo qui per poterlo mostrare in un box collassabile separato, se presente.
  const thinkingMatch = text?.match(/<think>([\s\S]*?)<\/think>/i);
  const thinking = thinkingMatch ? thinkingMatch[1].trim() : null;

  const tableStyles = {
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "1em",
      marginBottom: "1em",
      fontSize: "14px",
      backgroundColor: "white",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    th: {
      backgroundColor: "#3d85c6",
      color: "white",
      fontWeight: 600,
      padding: "12px 16px",
      textAlign: "left",
    },
    td: {
      padding: "12px 16px",
      border: "1px solid #e2e8f0",
      backgroundColor: "#f8fafc",
    },
    tr: {
      "&:nth-of-type(even)": {
        backgroundColor: "#f1f5f9",
      },
    },
  };

  return (
    <div
      key={`ai-response-${Math.random()}`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        marginBottom: "16px",
        maxWidth: "100%",
        paddingLeft: isUser ? "5%" : "0",
        paddingRight: isUser ? "0" : "5%",
        overflow: "auto",
        minHeight: "150px",
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: isUser ? "flex-end" : "flex-start",
          mb: 1,
        }}
      >
        {!isUser && isLoading ? (
          <>
            <Avatar
              alt="AI sta pensando"
              style={{
                width: 96,
                height: 96,
                backgroundColor: "transparent",
              }}
            >
              <Lottie
                options={{
                  loop: true,
                  autoplay: true,
                  animationData: robotThinking,
                  rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
                }}
                height={96}
                width={96}
              />
            </Avatar>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                ml: 1,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#fff",
                  color: "#000",
                  padding: "8px 12px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  position: "relative",
                  maxWidth: "200px",
                  lineHeight: "1.4",
                }}
              >
                Sto elaborando la tua risposta...
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: -8,
                    width: 0,
                    height: 0,
                    borderTop: "6px solid transparent",
                    borderBottom: "6px solid transparent",
                    borderRight: "8px solid #fff",
                    transform: "translateY(-50%)",
                  }}
                />
              </Box>
            </Box>
          </>
        ) : (
          <Avatar
            alt={isUser ? "Tu" : "AI"}
            style={{
              width: isUser ? null : 46,
              height: isUser ? null : 46,
              backgroundColor: isUser ? "#1976d2" : !isLoading ? "#e3f2fd" : "transparent",
              color: isUser ? "#fff" : "#1976d2",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {isLoading ? (
              <Lottie
                options={{
                  loop: false,
                  autoplay: false,
                  animationData: robotThinking,
                  rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
                }}
                height={46}
                width={46}
              />
            ) : isUser ? (
              "Tu"
            ) : (
              "AI"
            )}
          </Avatar>
        )}
      </Box>

      {!isLoading && (
        <div
          style={{
            backgroundColor: isUser ? "#fcfcfc" : "#fdfdfd",
            borderRadius: isUser ? "20px 0px 20px 20px" : "0px 20px 20px 20px",
            padding: "12px 16px",
            maxWidth: "100%",
            wordWrap: "break-word",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          {/* Testo principale senza <think> */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h3: ({ node, ...props }) => (
                <h3 style={{ fontSize: "1.2em", fontWeight: 600, margin: "1em 0 0.5em" }} {...props} />
              ),
              ul: ({ node, ...props }) => <ul style={{ margin: "0.5em 0", paddingLeft: "20px" }} {...props} />,
              li: ({ node, ...props }) => <li style={{ marginBottom: "0.2em" }} {...props} />,
              strong: ({ node, ...props }) => <strong style={{ fontWeight: "bold" }} {...props} />,
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                return !inline && match ? (
                  <SyntaxHighlighter style={docco} language={match[1]} PreTag="div" {...props}>
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                ) : (
                  <code
                    style={{
                      backgroundColor: "#f1f1f1",
                      padding: "0.2em 0.4em",
                      borderRadius: "6px",
                      fontSize: "0.9em",
                    }}
                    {...props}
                  />
                );
              },
              table: ({ node, ...props }) => (
                <div style={{ overflowX: "auto" }}>
                  <table style={tableStyles.table} {...props} />
                </div>
              ),
              th: ({ node, ...props }) => <th style={tableStyles.th} {...props} />,
              td: ({ node, ...props }) => <td style={tableStyles.td} {...props} />,
              tr: ({ node, ...props }) => <tr style={tableStyles.tr} {...props} />,
            }}
          >
            {cleanTextForDisplay(text)}
          </ReactMarkdown>

          {/* Bottone per mostrare/nascondere il ragionamento */}
          {thinking && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="grey"
                onClick={() => setShowThinking((prev) => !prev)}
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "none",
                  borderRadius: "20px",
                  height: "30px",
                }}
              >
                {showThinking ? "Nascondi ragionamento" : "Mostra ragionamento"}
              </Button>

              <Collapse in={showThinking}>
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                    fontSize: "13px",
                    color: "#495057",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {thinking}
                </Box>
              </Collapse>
            </Box>
          )}

        </div>
      )}

      {isLoading && !isUser && (
        <div></div>
      )}
    </div>
  );
};

export default AIChatResponse;
